import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Grid,
  Pagination,
  Stack,
  Tooltip,
} from "@mui/material";
import instance from "../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Topbar from "../../Layouts/Admin_layout/Admin_topbar";
import CustomDatePicker from "../../Components/Admin/CustomDatePicker";

const formatDate = (date) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US").format(date).replaceAll("/", "%2F");
};

const getDatesInRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates = [];

  while (start <= end) {
    dates.push(new Date(start));
    start.setDate(start.getDate() + 1);
  }

  return dates;
};

function isTimeInRange(time, startTime, endTime) {
  const date = new Date();
  const [hour, minute] = time.split(":").map(Number);
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);
  const checkTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hour,
    minute
  );
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    startHour,
    startMinute
  );
  const end = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    endHour,
    endMinute
  );
  return checkTime >= start && checkTime <= end;
}

function FieldOfficersDetails() {
  const { hash } = useParams();
  const tableTrackingRef = useRef(null);
  const [trackingData, setTrackingData] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const navigate = useNavigate();
  const [fieldOfficerDetails, setFieldOfficersDetails] = useState([]);
  const [officerTracking, setOfficerTracking] = useState([]);
  const [datesInRange, setDatesInRange] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
  });

  const handleChange = (event, value) => {
    setPage(value);
  };

  const fetchFieldOfficersDetails = async () => {
    try {
      const response = await instance(
        `field_officer/profile?fieldofficer_hash=${hash}`
      );
      setFieldOfficersDetails(response.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchFieldOfficersTracking = async () => {
    try {
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);
      const response = await instance(
        `/field_officer/getFieldOfficerTracking?start_date=${startDate}&end_date=${endDate}&fieldofficer_hash=${hash}&page=${page}`
      );
      setOfficerTracking(response.data.tracking);
      setPagination({
        totalPages: response.data.totalPages,
        currentPage: response.data.currentPage,
      });
      setTrackingData(true);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchFieldOfficersDetails();
  }, [hash]);

  useEffect(() => {
    if (startDate && endDate) {
      setDatesInRange(getDatesInRange(startDate, endDate));
      fetchFieldOfficersTracking();
    }
  }, [page]);

  useEffect(() => {
    if (trackingData && tableTrackingRef.current) {
      tableTrackingRef.current.scrollIntoView({ behaviour: "smooth" });
      setTrackingData(false);
    }
  }, [trackingData]);

  const timeSlots = [
    "8:00 to 8:30",
    "8:30 to 9:00",
    "9:00 to 9:30",
    "9:30 to 10:00",
    "10:00 to 10:30",
    "10:30 to 11:00",
    "11:00 to 11:30",
    "11:30 to 12:00",
    "12:00 to 12:30",
    "12:30 to 1:00",
    "1:00 to 1:30",
    "1:30 to 2:00",
    "2:00 to 2:30",
    "2:30 to 3:00",
    "3:00 to 3:30",
    "3:30 to 4:00",
    "4:00 to 4:30",
    "4:30 to 5:00",
    "5:00 to 5:30",
    "5:30 to 6:00",
  ];

  const getLocationForTimeSlot = (dayTracking, slot) => {
    const [start, end] = slot.split(" to ");
    const track = dayTracking.find((t) => {
      return isTimeInRange(t.time, start, end);
    });
    return track ? (
      <div>
        {track.location} <br />
        <Tooltip title="location">
          <a
            style={{ textDecoration: "none", color: "#2B2B2BCC" }}
            href={`https://www.google.com/maps/place/${track?.latitude}+${track?.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            Lat-{track.latitude} <br />
            Lon-{track.longitude}
          </a>
        </Tooltip>
      </div>
    ) : (
      "-"
    );
  };

  return (
    <div>
      <div style={{ padding: "20px 20px 0px 0px" }}>
        <Topbar />
      </div>
      <section style={{ padding: "20px 20px 0px 0px" }}>
        <div className="container-fluid">
          <div className="table_box">
            <TableContainer component={Paper}>
              <div className="table_inbox">
                <div className="add_user_list">
                  <div className="add_user_content">
                    <div
                      className="header-back"
                      style={{
                        fontFamily: "Poppins,sans-serif",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      Field Officers Details
                      <button
                        className="back-btn"
                        onClick={() => navigate("/admin/FieldOfficers")}
                      >
                        Back
                      </button>
                    </div>
                    <div className="date-container">
                      <div className="dates">
                        <Grid container spacing={3}>
                          <Grid item md={6} className="customDatePicker">
                            <CustomDatePicker
                              label="Start Date "
                              value={startDate}
                              onChange={(date) => setStartDate(date.toDate())}
                            />
                          </Grid>
                          <Grid item md={6} className="customDatePicker">
                            <CustomDatePicker
                              label="End Date"
                              value={endDate}
                              onChange={(date) => setEndDate(date.toDate())}
                            />
                          </Grid>
                        </Grid>
                      </div>
                      <button
                        className="tracking-btn"
                        onClick={fetchFieldOfficersTracking}
                      >
                        Track FO
                      </button>
                    </div>
                  </div>
                </div>
                <Table
                  className="adduser_table user_table product_table"
                  aria-label="simple table"
                >
                  <TableHead>
                    <TableRow className="table_head">
                      <TableCell align="center" className="second-child">
                        Employee ID
                      </TableCell>
                      <TableCell align="center" className="second-child">
                        Field Officer Name
                      </TableCell>
                      <TableCell align="center" className="third-child">
                        Email
                      </TableCell>
                      <TableCell align="center">Mobile</TableCell>
                      <TableCell align="center">Vendor Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="table_body">
                    <TableRow key={fieldOfficerDetails._id}>
                      <TableCell align="center">
                        {fieldOfficerDetails.employee_id}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.name}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.email}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.mobile_no}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.vendor?.name}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </div>
        </div>
      </section>
      <section style={{ padding: "20px 20px 0px 0px" }}>
        <div className="container-fluid">
          <div className="table_box" ref={tableTrackingRef}>
            <TableContainer component={Paper}>
              <div className="table-inbox">
                <div className="add_user_list">
                  <div className="add-user-content">
                    <div className="table-tracking">
                      <Table
                        className="adduser_table user_table product_table"
                        aria-label="simple table"
                      >
                        <TableHead>
                          <TableRow className="table_head">
                            <TableCell align="center" className="second-child">
                              Date
                            </TableCell>
                            {timeSlots.map((slot, index) => (
                              <TableCell key={index}>{slot}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody className="table_body">
                          {officerTracking.map((datedata) => (
                            <TableRow key={datedata.date}>
                              <TableCell align="center">
                                {datedata.date}
                              </TableCell>
                              {timeSlots.map((slot, index) => (
                                <TableCell
                                  align="center"
                                  sx={{
                                    borderLeft: "1px solid #82828299",
                                    borderRight: "1px solid #82828299",
                                  }}
                                  key={index}
                                >
                                  {
                                    getLocationForTimeSlot(
                                      datedata.day_tracking,
                                      slot
                                    )
                                    // .split(",")
                                    // .reduce(
                                    //   (acc, part, idx, arr) => (
                                    //     <>
                                    //       {acc}
                                    //       <div key={idx}>
                                    //         {part.trim()}
                                    //         {/* {idx < arr.length - 1 && <br />} */}
                                    //       </div>
                                    //     </>
                                    //   ),
                                    //   null
                                  }
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                  </div>
                </div>
              </div>
            </TableContainer>
          </div>
          <div className="pagination_table">
            <Pagination
              shape="rounded"
              variant="outlined"
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handleChange}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export default FieldOfficersDetails;
